import IconDownload from 'assets/icons/IconDownload';
import { ErrorAssessmentModal } from 'components/ErrorAssessmentModal';
// Import Data (Resync) button hidden until further notice.
// import { ResyncButton } from 'components/ResyncButton';
import React, { useEffect, useState, useCallback } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  downloadReport,
  getAssesmentReportsList,
} from 'store/assessmentSingle/actions';
import { useTooltipWithScrollClose } from '../../hooks/useTooltipWithScrollClose';
import './styles.scss';
import Loader from './Loader';

interface IProps {
  assesmentID: number;
  // Every assessment of the package. Reports are attached to individual
  // package members, so looking only at the opened one hides them.
  assessmentIDs?: number[];
  isReadOnly?: boolean;
  onBeforeSync?: () => Promise<void>;
}

const DownloadReportButton = ({
  assesmentID,
  assessmentIDs,
  isReadOnly,
  onBeforeSync,
}: IProps) => {
  const { t, i18n } = useTranslation();
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [reportsList, setReportsList] = useState([]);
  const [loading, setLoading] = useState(true);
  // Controlled dropdown so it closes on page scroll (same pattern as info-box tooltips)
  const [showDropdown, setShowDropdown] = useTooltipWithScrollClose();

  // Joined into a string so a freshly built array does not retrigger the fetch
  const idsKey = (assessmentIDs?.length ? assessmentIDs : [assesmentID]).join(
    ',',
  );

  const getAssessmentReports = useCallback(async () => {
    const ids = idsKey.split(',').map(Number);
    setLoading(true);
    setReportsList([]);
    let pending = ids.length;
    // Each package member is applied as it answers. Awaiting all of them would
    // keep the button hidden behind the slowest sibling request.
    ids.forEach(async id => {
      try {
        const reports = await getAssesmentReportsList(id);
        if (reports?.length) {
          // Keep the owning assessment next to each report - RunReport needs it
          setReportsList(prev => [
            ...prev,
            ...reports
              .filter(report => !prev.some(r => r.ID === report.ID))
              .map(report => ({ ...report, assessmentID: id })),
          ]);
        }
      } catch (error) {
        console.log(error);
      } finally {
        pending -= 1;
        if (pending === 0) {
          setLoading(false);
        }
      }
    });
  }, [idsKey]);

  useEffect(() => {
    getAssessmentReports();
  }, [getAssessmentReports]);

  const handleDownload = async (
    reportAssessmentID: number,
    reportId: number,
    reportName: string,
  ) => {
    try {
      setLoading(true);
      const response = await downloadReport(reportAssessmentID, reportId);
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Report-${reportName}_Assesment-${reportAssessmentID}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setLoading(false);

    } catch (error) {
      console.log(error);
      setShowErrorPopup(true);
      setLoading(false);

    }
  };

  //@ts-ignore
  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    //@ts-ignore
    <a
      href=""
      //@ts-ignore
      ref={ref}
      onClick={e => {
        e.preventDefault();
        onClick(e);
      }}
    >
      <button className="btn action-secondary">
        <div>{loading ? <Loader /> : t('generate_report')}</div>
      </button>
    </a>
  ));

  return (
    <>
      <div className="download-report-button">
        <div className="buttons-container">
          {/* Import Data (Resync) button hidden until further notice. */}
          {/* {!isReadOnly && (
            <ResyncButton assessmentId={assesmentID} onBeforeSync={onBeforeSync} />
          )} */}
          {reportsList?.length > 0 && (
            <Dropdown show={showDropdown} onToggle={setShowDropdown}>
              <Dropdown.Toggle as={CustomToggle} />
              <Dropdown.Menu title="">
                {loading ? (
                  <Loader />
                ) : (
                  reportsList.map((report, index) => (
                    <React.Fragment key={report.ID}>
                      <Dropdown.Item as="div" className="report-dropdown-item">
                        <IconDownload color={'black'} />
                        <a
                          className="download-report-link"
                          onClick={() =>
                            handleDownload(
                              report.assessmentID,
                              report.ID,
                              report.Name,
                            )
                          }
                        >
                          Download {report.Name}
                        </a>
                      </Dropdown.Item>
                      {index !== reportsList.length - 1 ? (
                        <Dropdown.Divider />
                      ) : (
                        <></>
                      )}
                    </React.Fragment>
                  ))
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>
      <ErrorAssessmentModal
        isOpen={showErrorPopup}
        onExit={() => setShowErrorPopup(false)}
        text={`Error downloading document`}
      />
    </>
  );
};

export default DownloadReportButton;
